// src/components/Status/StatusComponents.jsx
import { useState, useRef, useLayoutEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { useColor } from "../../hooks/color/useColor";
import {
  FiClock,
  FiCalendar,
  FiX,
  FiCheck,
  FiCheckCircle,
  FiPlay,
  FiUserX,
  FiRefreshCw,
  FiLogIn,
  FiLogOut,
} from "react-icons/fi";
import { TokenContext } from "../../context/TokenContext";

// Status Configuration (static parts)
const STATUS_CONFIG = {
  mapping: {
    displayToApi: {
      Booked: "booked",
      Confirmed: "confirmed",
      Started: "started",
      Completed: "completed",
      Cancel: "cancel",
      Cancelled: "cancel",
      "No Show": "no show",
      Pending: "pending",
      Checkin: "checkin",
      Checkout: "checkout",
      Closed: "closed",
    },
    apiToDisplay: {
      booked: "Booked",
      confirmed: "Confirmed",
      started: "Started",
      completed: "Completed",
      cancel: "Cancel",
      cancelled: "Cancel",
      "no show": "No Show",
      "no-show": "No Show",
      pending: "Pending",
      checkin: "Checkin",
      checkout: "Checkout",
      draft: "Pending",
      closed: "Closed",
    },
  },

  // Fallback colors (used when dynamic colors aren't available)
  colors: {
    Booked: "bg-blue-100 text-blue-800 border-blue-300",
    Confirmed: "bg-green-100 text-green-800 border-green-300",
    Started: "bg-orange-100 text-orange-800 border-orange-300",
    Completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Cancel: "bg-red-100 text-red-800 border-red-300",
    Cancelled: "bg-red-100 text-red-800 border-red-300",
    "No Show": "bg-purple-100 text-purple-800 border-purple-300",
    Pending: "bg-gray-100 text-gray-800 border-gray-300",
    Checkin: "bg-cyan-100 text-cyan-800 border-cyan-300",
    Checkout: "bg-amber-100 text-amber-800 border-amber-300",
  },

  icons: {
    Booked: FiCalendar,
    Confirmed: FiCheckCircle,
    Started: FiPlay,
    Completed: FiCheck,
    Cancel: FiX,
    "No Show": FiUserX,
    Pending: FiClock,
    Checkin: FiLogIn,
    Checkout: FiLogOut,
  },
};

// Dynamic transitions based on employee status
const getTransitions = (isEmployee) => ({
  booked: ["Confirmed", "Cancel"],
  confirmed: ["Started", "No Show", "Cancel"],
  started: ["Completed", "Cancel"],
  completed: isEmployee ? [] : ["closed"],
  cancel: [],
  "no show": [],
  pending: ["Confirmed", "Cancel"],
  checkin: ["Started", "Cancel", "Completed"],
  checkout: ["Completed", "Cancel"],
});

// Payment Status Configuration
const PAYMENT_STATUS_CONFIG = {
  paid: { color: "bg-green-100 text-green-800", label: "Paid" },
  partial: { color: "bg-yellow-100 text-yellow-800", label: "Partial" },
  unpaid: { color: "bg-red-100 text-red-800", label: "Unpaid" },
};

// Utility Functions
export const StatusUtils = {
  toApi: (displayStatus) => {
    if (!displayStatus) return "booked";
    return (
      STATUS_CONFIG.mapping.displayToApi[displayStatus] ||
      displayStatus.toLowerCase()
    );
  },

  toDisplay: (apiStatus) => {
    if (!apiStatus || apiStatus === "") return "Booked";

    const lowerCaseStatus = apiStatus.toLowerCase();
    return (
      STATUS_CONFIG.mapping.apiToDisplay[lowerCaseStatus] ||
      (apiStatus
        ? apiStatus.charAt(0).toUpperCase() + apiStatus.slice(1)
        : "Booked")
    );
  },

  getColor: (status) => {
    const displayStatus = StatusUtils.toDisplay(status);
    return STATUS_CONFIG.colors[displayStatus] || STATUS_CONFIG.colors.Pending;
  },

  getIcon: (status) => {
    const displayStatus = StatusUtils.toDisplay(status);
    return STATUS_CONFIG.icons[displayStatus] || STATUS_CONFIG.icons.Pending;
  },

  isValidTransition: (fromStatus, toStatus, isEmployee = false) => {
    const fromApi = StatusUtils.toApi(fromStatus);
    const toApi = StatusUtils.toApi(toStatus);
    const transitions = getTransitions(isEmployee);
    const allowedTransitions = transitions[fromApi] || [];
    return allowedTransitions.includes(toApi);
  },

  getAllowedTransitions: (currentStatus, isEmployee = false) => {
    const apiStatus = StatusUtils.toApi(currentStatus);
    const transitions = getTransitions(isEmployee);
    const allowedApiStatuses = transitions[apiStatus] || [];
    return allowedApiStatuses.map(StatusUtils.toDisplay);
  },
};

// Status Badge Component with Dynamic Colors
export const StatusBadge = ({
  status,
  size = "md",
  showIcon = true,
  className = "",
}) => {
  const { getColor } = useColor();
  const displayStatus = StatusUtils.toDisplay(status);
  const IconComponent = StatusUtils.getIcon(status);

  // Try to get dynamic color, fall back to static color
  const dynamicColor = getColor("statusColors", displayStatus);
  const statusColor = dynamicColor || StatusUtils.getColor(status);

  const sizeClasses = {
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
    lg: "px-4 py-2 text-base",
  };

  // Check if dynamic color is available (hex format)
  const isHexColor = dynamicColor && dynamicColor.startsWith("#");

  if (isHexColor) {
    return (
      <span
        className={`
          inline-flex items-center gap-2 rounded-full font-semibold border shadow-sm
          ${sizeClasses[size]}
          ${className}
        `}
        style={{
          backgroundColor: dynamicColor,
          borderColor: dynamicColor,
          color: "#000000",
        }}
      >
        {showIcon && <IconComponent className="w-3 h-3" />}
        {displayStatus}
      </span>
    );
  }

  return (
    <span
      className={`
        inline-flex items-center gap-2 rounded-full font-semibold border shadow-sm
        ${statusColor} 
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {showIcon && <IconComponent className="w-3 h-3" />}
      {displayStatus}
    </span>
  );
};

// Payment Status Badge Component
export const PaymentStatusBadge = ({ status, size = "md", className = "" }) => {
  const { getColor } = useColor();

  const statusKey = status?.toLowerCase();
  const dynamicColor = getColor("paymentStatus", statusKey);

  const config =
    PAYMENT_STATUS_CONFIG[statusKey] || PAYMENT_STATUS_CONFIG.unpaid;
  const sizeClass = size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1 text-sm";

  const isHexColor = dynamicColor && dynamicColor.startsWith("#");

  if (isHexColor) {
    return (
      <span
        className={`rounded-full font-semibold ${sizeClass} ${className}`}
        style={{
          backgroundColor: dynamicColor,
          color: "#000000",
          border: `1px solid ${dynamicColor}`,
        }}
      >
        {config.label}
      </span>
    );
  }

  return (
    <span
      className={`rounded-full font-semibold ${config.color} ${sizeClass} ${className}`}
    >
      {config.label}
    </span>
  );
};

// Portal-based dropdown menu
const DropdownMenu = ({ isOpen, onClose, position, children }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop to close dropdown */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      {/* Dropdown menu */}
      <div
        className="absolute z-[9999] bg-white rounded-xl shadow-xl border border-gray-200 py-2 w-44"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body,
  );
};

// Status Dropdown Component with Dynamic Colors
export const StatusDropdown = ({
  value,
  onChange,
  disabledStatuses = [],
  onStartService,
  currentStatus,
  size = "md",
  className = "",
}) => {
  const { getColor } = useColor();
  const { isEmployee } = useContext(TokenContext); // ✅ Hook allowed here
  const [open, setOpen] = useState(false);
  const buttonRef = useRef();
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate dropdown position
  const calculatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right - 176 + window.scrollX,
      });
    }
  };

  useLayoutEffect(() => {
    if (open) {
      calculatePosition();
      window.addEventListener("resize", calculatePosition);
    }
    return () => {
      window.removeEventListener("resize", calculatePosition);
    };
  }, [open]);

  const displayValue = StatusUtils.toDisplay(value);
  const allowedStatuses = StatusUtils.getAllowedTransitions(
    currentStatus || value,
    isEmployee, // Pass employee flag
  );
  const dynamicColor = getColor("statusColors", displayValue);

  // If status is Cancel, render static badge
  if (displayValue === "Cancel") {
    return <StatusBadge status="Cancel" size={size} className={className} />;
  }

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm min-w-[100px]",
    md: "px-4 py-2 text-sm min-w-[120px]",
    lg: "px-4 py-2 text-base min-w-[140px]",
  };

  const isHexColor = dynamicColor && dynamicColor.startsWith("#");
  const fallbackColor = StatusUtils.getColor(value);

  return (
    <div className="status-dropdown">
      <button
        ref={buttonRef}
        type="button"
        className={`
          w-full px-2 py-1 rounded font-semibold shadow-sm border min-w-[60px] flex items-center justify-between gap-1 transition-all 
          ${sizeClasses[size]}
          ${open ? "ring-2 ring-offset-2 ring-blue-400" : ""}
          ${!isHexColor ? fallbackColor : ""}
          ${className}
        `}
        style={{
          backgroundColor: dynamicColor,
          borderColor: "#000",
          color: "#000000",
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span>{displayValue}</span>
        <svg
          className="w-4 h-4 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <DropdownMenu
        isOpen={open}
        onClose={() => setOpen(false)}
        position={position}
      >
        {allowedStatuses.map((status) => {
          const isSelected = displayValue === status;
          const IconComponent = StatusUtils.getIcon(status);
          const isDisabled = disabledStatuses.includes(status);
          const statusDynamicColor = getColor("statusColors", status);
          const isStatusHexColor =
            statusDynamicColor && statusDynamicColor.startsWith("#");

          return (
            <button
              key={status}
              type="button"
              disabled={isDisabled}
              onClick={(e) => {
                if (!isDisabled) {
                  e.stopPropagation();
                  onChange(status);

                  if (status === "Started") {
                    onStartService?.();
                  }

                  setOpen(false);
                }
              }}
              className={`
                w-full flex items-center gap-2 px-4 py-2 text-left rounded-lg transition-all font-medium
                ${isDisabled ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 text-gray-700"}
                ${isSelected ? "bg-blue-50 text-blue-700 font-semibold" : "bg-white"}
              `}
            >
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: isStatusHexColor
                    ? statusDynamicColor
                    : status === "Booked"
                      ? "#2563eb"
                      : status === "Confirmed"
                        ? "#16a34a"
                        : status === "Started"
                          ? "#ea580c"
                          : status === "Completed"
                            ? "#059669"
                            : status === "Cancel"
                              ? "#dc2626"
                              : status === "No Show"
                                ? "#a855f7"
                                : "#6b7280",
                }}
              />
              <IconComponent className="w-3 h-3" />
              <span
                className={
                  status === "Cancel" || status === "No Show"
                    ? "text-red-500"
                    : ""
                }
              >
                {status}
              </span>
            </button>
          );
        })}
      </DropdownMenu>
    </div>
  );
};

// Status Indicator Component with Dynamic Colors
export const StatusIndicator = ({ status, size = "md", className = "" }) => {
  const { getColor } = useColor();
  const displayStatus = StatusUtils.toDisplay(status);
  const dynamicColor = getColor("statusColors", displayStatus);

  const sizeClasses = {
    sm: "w-2 h-2",
    md: "w-3 h-3",
    lg: "w-4 h-4",
  };

  const colorClasses = {
    Booked: "bg-blue-500",
    Confirmed: "bg-green-500",
    Started: "bg-orange-500",
    Completed: "bg-emerald-500",
    Cancel: "bg-red-500",
    "No Show": "bg-purple-500",
    Pending: "bg-gray-500",
    Checkin: "bg-cyan-500",
    Checkout: "bg-amber-500",
  };

  const isHexColor = dynamicColor && dynamicColor.startsWith("#");

  return (
    <div
      className={`
        rounded-full 
        ${sizeClasses[size]} 
        ${
          !isHexColor ? colorClasses[displayStatus] || colorClasses.Pending : ""
        }
        ${className}
      `}
      style={isHexColor ? { backgroundColor: dynamicColor } : {}}
      title={displayStatus}
    />
  );
};

export default {
  StatusUtils,
  StatusBadge,
  PaymentStatusBadge,
  StatusDropdown,
  StatusIndicator,
};